import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCustomerGroups } from '../../hooks/queries';
import {
  buildCustomerGroupListSearchParams,
  getCustomerGroupHighlightId,
  isCustomerGroupListUrlStateSynced,
  parseCustomerGroupListSearchParams,
  type CustomerGroupDetailLocationState,
} from '../../utils/customer-navigation';

function buildQuerySignature(page: number, limit: number, searchTerm: string): string {
  return `${page}|${limit}|${searchTerm.trim()}`;
}

export function CustomerGroupList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightRowRef = useRef<HTMLTableRowElement>(null);
  const skipSearchHydrationFromUrlRef = useRef(false);

  const initialUrlState = parseCustomerGroupListSearchParams(searchParams);
  const [page, setPage] = useState(initialUrlState.page);
  const [limit, setLimit] = useState(initialUrlState.limit);
  const [searchInput, setSearchInput] = useState(initialUrlState.searchTerm);
  const [appliedSearch, setAppliedSearch] = useState(initialUrlState.searchTerm);
  const [searchGeneration, setSearchGeneration] = useState(0);
  const [pendingQuerySignature, setPendingQuerySignature] = useState<string | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    const { page: urlPage, searchTerm: urlSearchTerm, limit: urlLimit } =
      parseCustomerGroupListSearchParams(searchParams);
    setPage(urlPage);
    setLimit(urlLimit);

    if (skipSearchHydrationFromUrlRef.current) {
      skipSearchHydrationFromUrlRef.current = false;
      return;
    }

    setSearchInput(urlSearchTerm);
    setAppliedSearch(urlSearchTerm);
    setPendingQuerySignature(buildQuerySignature(urlPage, urlLimit, urlSearchTerm));
  }, [searchParams]);

  useEffect(() => {
    const listState = {
      page,
      searchTerm: appliedSearch,
      limit,
    };

    if (isCustomerGroupListUrlStateSynced(searchParams, listState)) {
      return;
    }

    const urlState = parseCustomerGroupListSearchParams(searchParams);
    const preserveHighlight =
      urlState.searchTerm.trim() === appliedSearch.trim() ? searchParams : undefined;

    skipSearchHydrationFromUrlRef.current = true;
    setSearchParams(
      buildCustomerGroupListSearchParams(
        {
          page,
          searchTerm: appliedSearch,
          limit,
        },
        preserveHighlight,
      ),
      { replace: true },
    );
  }, [page, appliedSearch, limit, searchParams, setSearchParams]);

  const highlightedGroupId = useMemo(
    () => getCustomerGroupHighlightId(location.search, location.state),
    [location.search, location.state],
  );

  const normalizedSearch = appliedSearch.trim() || undefined;
  const queryFilters = normalizedSearch ? { name: normalizedSearch } : undefined;

  const {
    data: pageResponse,
    isLoading,
    isFetching,
    isPlaceholderData,
    error: pageQueryError,
  } = useCustomerGroups(page, limit, queryFilters, searchGeneration);

  const groups: Array<Record<string, unknown>> =
    pageResponse?.data && Array.isArray(pageResponse.data) ? pageResponse.data : [];

  useEffect(() => {
    if (highlightedGroupId !== undefined && highlightRowRef.current) {
      highlightRowRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [highlightedGroupId, groups.length]);

  const error = pageQueryError
    ? String((pageQueryError as { message?: string }).message ?? pageQueryError)
    : pageResponse?.success === false
      ? String(pageResponse.message ?? 'Failed to fetch customer groups')
      : null;

  const pagination = pageResponse?.pagination;
  const totalCount = pagination?.total;
  const responsePage = pagination?.page ?? page;
  const displayPage = isPlaceholderData ? responsePage : page;
  const isPageTransition = isFetching && isPlaceholderData;
  const hasMore =
    pagination?.hasMore !== undefined
      ? pagination.hasMore
      : groups.length === limit;

  const calculateTotalDiscounts = (group: Record<string, unknown>): number => {
    const rules = group.discount_rules;
    return Array.isArray(rules) ? rules.length : 0;
  };

  const startIndex = groups.length > 0 ? (displayPage - 1) * limit + 1 : 0;
  const endIndex = groups.length > 0 ? startIndex + groups.length - 1 : 0;

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setHasLoadedOnce(true);
      setPendingQuerySignature(null);
    }
  }, [isLoading, isFetching]);

  const isFilterTransition =
    pendingQuerySignature !== null &&
    groups.length === 0 &&
    (isFetching || isLoading);
  const loadingState = !hasLoadedOnce && isLoading && groups.length === 0;
  const isTablePending = isPageTransition || isFilterTransition;

  const buildGroupDetailState = (): CustomerGroupDetailLocationState => ({
    listPage: page,
    listSearch: normalizedSearch,
    listLimit: limit,
  });

  const handleSearchSubmit = (event?: React.FormEvent): void => {
    event?.preventDefault();
    setAppliedSearch(searchInput);
    setPage(1);
    setSearchGeneration((generation) => generation + 1);
    setPendingQuerySignature(buildQuerySignature(1, limit, searchInput));
  };

  const handleClearSearch = (): void => {
    setSearchInput('');
    setAppliedSearch('');
    setPage(1);
    setSearchGeneration((generation) => generation + 1);
    setPendingQuerySignature(buildQuerySignature(1, limit, ''));
  };

  const handlePageChange = (nextPage: number): void => {
    setPage(nextPage);
    setPendingQuerySignature(buildQuerySignature(nextPage, limit, appliedSearch));
  };

  const handleLimitChange = (nextLimit: number): void => {
    setLimit(nextLimit);
    setPage(1);
    setPendingQuerySignature(buildQuerySignature(1, nextLimit, appliedSearch));
  };

  if (loadingState) {
    return (
      <div className="page-container">
        <div className="loading-state">
          <div className="spinner" />
          Loading groups…
        </div>
      </div>
    );
  }

  if (error && groups.length === 0) {
    return (
      <div className="page-container">
        <div className="error-banner">⚠ {error}</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Customer Groups</h1>
          <p className="page-subtitle">
            {groups.length > 0
              ? `${startIndex}–${endIndex} of ${totalCount ?? '?'} groups`
              : 'No groups'}
            {isTablePending ? ' · loading…' : ''}
          </p>
        </div>

        <div className="page-actions">
          <form
            className="search-wrap"
            onSubmit={handleSearchSubmit}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <span className="search-icon">🔍</span>
            <input
              className="search-input"
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search groups…"
            />
            <button type="submit" className="btn btn-primary btn-sm">
              Search
            </button>
            {appliedSearch.trim() ? (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleClearSearch}
              >
                Clear
              </button>
            ) : null}
          </form>

          <select
            className="form-input"
            style={{ width: 'auto' }}
            value={limit}
            onChange={(e) => {
              handleLimitChange(parseInt(e.target.value, 10));
            }}
          >
            <option value={10}>10 / page</option>
            <option value={25}>25 / page</option>
            <option value={50}>50 / page</option>
          </select>

          <div className="pagination">
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1 || isTablePending}
            >
              ←
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => handlePageChange(page + 1)}
              disabled={!hasMore || isTablePending}
            >
              →
            </button>
          </div>
        </div>
      </div>

      {isFilterTransition ? (
        <div className="table-wrap">
          <div className="loading-state">
            <div className="spinner" />
            Loading groups…
          </div>
        </div>
      ) : groups.length === 0 ? (
        <div className="table-wrap">
          <div className="empty-state">
            <div className="empty-state-icon">🏷</div>
            <p className="empty-state-title">No customer groups found</p>
          </div>
        </div>
      ) : (
        <div className={`table-wrap${isTablePending ? ' table-wrap--pending' : ''}`}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Discount Rules</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => {
                const groupId = group.id as number;
                const ruleCount = calculateTotalDiscounts(group);
                const detailState = buildGroupDetailState();

                return (
                  <tr
                    key={groupId}
                    ref={groupId === highlightedGroupId ? highlightRowRef : undefined}
                    className={`clickable${groupId === highlightedGroupId ? ' highlight-row' : ''}`}
                    onClick={() =>
                      navigate(`/customer-groups/${groupId}`, { state: detailState })
                    }
                  >
                    <td>
                      <Link
                        to={`/customer-groups/${groupId}`}
                        state={detailState}
                        className="text-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {String(group.name ?? `Group ${groupId}`)}
                      </Link>
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>
                      {ruleCount > 0 ? (
                        <span className="badge badge-info">
                          {ruleCount} rule{ruleCount !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>None</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
